import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AUDIO_TYPE, ContentConfigEditOrInsert } from '@/types/custom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ContentTranscriptField } from '@/components/admin/content-transcript-field';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface ConfigEditViewProps {
  config: ContentConfigEditOrInsert;
  onUpdate: (updatedConfig: ContentConfigEditOrInsert) => void;
  onDelete: () => void;
  musicAllowed: boolean;
}

export const nameConfig = (config: ContentConfigEditOrInsert) => {
  let name = config.music ? 'Muziek' : 'Stem';
  if (config.voice_name) {
    name += ` (${config.voice_name})`;
  }
  return name;
};

/**
 * View or edit a content config for a given meditation.
 */
const ConfigEditView: React.FC<ConfigEditViewProps> = ({
  config,
  onUpdate,
  onDelete,
  musicAllowed,
}) => {
  const audioType: AUDIO_TYPE = config.voice ? 'voice' : 'music';
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const name = config.voice ? `Stem ${config.voice_name}` : 'muziek';
  return (
    <div className="space-y-4" style={{ padding: '0px 18px 0px 18px' }}>
      <div>
        <Label htmlFor="audioUrl">Audio URL</Label>
        <Input
          id="audioUrl"
          name="audioUrl"
          type="url"
          value={config.audio_link || ''}
          onChange={(e) =>
            onUpdate({ ...structuredClone(config), audio_link: e.target.value })
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Audio Type</Label>
          <Select
            value={audioType}
            onValueChange={(value) => {
              const isVoice = value === 'voice';
              const voice_name = isVoice ? config.voice_name : null;
              onUpdate({
                ...structuredClone(config),
                voice: isVoice,
                music: !isVoice,
                transcription_url: isVoice ? config.transcription_url : null,
                voice_name,
              });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecteer type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="music" disabled={!musicAllowed}>
                muziek
              </SelectItem>
              <SelectItem value="voice">stem</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {audioType === 'voice' && (
          <div>
            <Label htmlFor="audioUrl">Stem Naam</Label>
            <Input
              id="voiceName"
              name="voiceName"
              type="string"
              value={config.voice_name || ''}
              onChange={(e) =>
                onUpdate({
                  ...structuredClone(config),
                  voice_name: e.target.value,
                })
              }
            />
          </div>
        )}
      </div>

      {audioType === 'voice' && (
        <ContentTranscriptField
          value={config.transcription_url || ''}
          onChange={(url) =>
            onUpdate({ ...structuredClone(config), transcription_url: url })
          }
        />
      )}

      <div className="flex justify-start">
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogTrigger asChild>
            <Button variant="destructive">
              Verwijderen {nameConfig(config)}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Verwijderen {nameConfig(config)}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Dit wordt pas definitief nadat u het formulier hebt opgeslagen.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuleren</AlertDialogCancel>
              <AlertDialogAction onClick={onDelete}>
                Verwijderen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default ConfigEditView;
